/**
 * Primitives shared by every domain group: identifiers, the cursor-pagination
 * envelope, the universal request headers, and the problem+json error body.
 */

import { z } from 'zod';
import { ERROR_CODES } from '../errors.ts';
import { clientKindSchema } from './enums.ts';

// --- primitives --------------------------------------------------------------

/** All primary keys are uuidv7 generated by Postgres; validated as any RFC 9562 uuid. */
export const uuidSchema = z.uuid();

/** RFC 3339 timestamp. `offset: true` also accepts `+02:00`-style offsets, not just `Z`. */
export const isoDateTimeSchema = z.iso.datetime({ offset: true });

/** Opaque base64 of `(sort_key, id)`. Clients must never parse it. */
export const cursorSchema = z.string().min(1).max(512);

/** Client-generated ULID, echoed in every response and every server log line. */
export const correlationIdSchema = z.string().min(8).max(64);

/** `Idempotency-Key`. Server stores `(tenant_id, key) -> response` for 24h. */
export const idempotencyKeySchema = z.string().min(8).max(255);

export const timezoneSchema = z.string().min(1).max(64);

/** `HH:MM` in the user's local timezone. */
export const localTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM');

/** A free-form JSON blob (smart-list queries, interaction metadata). */
export const jsonObjectSchema = z.record(z.string(), z.unknown());

/**
 * Non-blank text. `.min(1)` alone accepts `"   "`, which is exactly the value that
 * violates the `contacts_first_name_nonempty` CHECK constraint in Postgres.
 */
export function nonBlankString(max: number, label = 'value') {
  return z
    .string()
    .min(1)
    .max(max)
    .refine((value) => value.trim().length > 0, `${label} must not be blank`);
}

// --- pagination --------------------------------------------------------------

export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 200;

/** Cursor pagination everywhere; offset paginates incorrectly under concurrent sync. */
export const paginationInputSchema = z.object({
  limit: z.int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  cursor: cursorSchema.optional(),
});

export type PaginationInput = z.output<typeof paginationInputSchema>;

/**
 * The one list envelope. `nextCursor` is `null` — not absent — on the last page so
 * clients can distinguish "end of data" from "field missing".
 */
export function paginated<TItem extends z.ZodType>(item: TItem) {
  return z.object({
    items: z.array(item),
    nextCursor: cursorSchema.nullable(),
  });
}

// --- headers -----------------------------------------------------------------

/**
 * Required on every POST/PATCH that ingests client data. Declared here for the Hono
 * adapter and the api-client to share; it is enforced by middleware rather than being
 * folded into each procedure input, so procedures keep oRPC's `compact` input shape.
 */
export const idempotencyKeyHeaderSchema = z.object({
  'idempotency-key': idempotencyKeySchema,
});

/** Universal request headers (§1.5 of the clients design). */
export const requestHeadersSchema = z.object({
  'x-client': clientKindSchema,
  'x-client-version': z.string().min(1).max(32),
  'x-correlation-id': correlationIdSchema.optional(),
});

export type RequestHeaders = z.output<typeof requestHeadersSchema>;

// --- errors ------------------------------------------------------------------

export const errorCodeSchema = z.enum(ERROR_CODES);

export const appErrorSchema = z.object({
  code: errorCodeSchema,
  message: z.string(),
  retryable: z.boolean(),
  details: z.unknown().optional(),
  correlationId: correlationIdSchema,
});

/** RFC 7807 `application/problem+json`, extended with our stable `code`. */
export const problemDetailsSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.int().min(400).max(599),
  detail: z.string().optional(),
  instance: z.string().optional(),
  code: errorCodeSchema,
  retryable: z.boolean(),
  correlationId: correlationIdSchema,
  details: z.unknown().optional(),
});

// --- trivial responses -------------------------------------------------------

/** Acknowledgement for mutations with nothing useful to return. */
export const okSchema = z.object({ ok: z.literal(true) });

/** Acknowledgement carrying the row count a bulk mutation touched. */
export const countSchema = z.object({
  ok: z.literal(true),
  count: z.int().min(0),
});
